/**
 * @import { CruiseSearchResult } from '../types/cruise-search-result'
 */
import { useCallback, useMemo, useState } from 'react';
import { useCruises } from './use-cruises';

/**
 * @typedef {object} Page
 * @property {CruiseSearchResult[]} cruises
 * @property {{ page: number; pageSize: number; totalRecords: number; totalPages: number }} pagination
 */

const minPageSize = 1;

/**
 * @param {number} [pageSize]
 */
export function usePaginatedCruises(pageSize = 20) {
  const [page, setPage] = useState(0);
  const cruises = useCruises();
  const clampedPageSize = Math.max(pageSize, minPageSize);
  const maxPage = cruises.data ? Math.floor(cruises.data.length / clampedPageSize) : 0;
  const hasNextPage = page < maxPage;

  const fetchNextPage = useCallback(() => {
    if (!hasNextPage) {
      return;
    }

    setPage(page + 1);
  }, [page, hasNextPage]);

  return useMemo(() => {
    const data = cruises.data;
    if (!data) {
      return { ...cruises, data: undefined, hasNextPage, fetchNextPage };
    }

    /** @type {Page[]} */
    const pages = [];
    for (let i = 0; i < page + 1; i++) {
      const pagination = {
        page: i,
        pageSize: clampedPageSize,
        totalRecords: data.length,
        totalPages: maxPage + 1,
      };
      const start = i * clampedPageSize;
      const dataPage = data.slice(start, start + clampedPageSize);
      pages.push({ cruises: dataPage, pagination });
    }
    return { ...cruises, data: { pages }, hasNextPage, fetchNextPage };
  }, [page, cruises, hasNextPage, fetchNextPage, maxPage, clampedPageSize]);
}
